/**
 * 3. pálya — MAGKAMRA
 *
 * Térdramaturgia (C): az első két pálya lineáris volt; ez **gyűrű**.
 * A süllyesztett magkamra körül négy folyosó fut körbe, és mind a négy
 * oldalról be lehet lépni. Ez szándékos: az ostromgép elől nem elbújni
 * kell, hanem **körözni** — a tér maga tanítja meg a helyes taktikát.
 *
 * Az ív: bemelegítés → körfolyosó (itt tanulja meg a játékos, hogy a
 * gyűrű körbeér) → kulcsterem az első ostromgéppel, zsákutcában (ahol a
 * körözés NEM működik, ezért itt kell először tudatosan tűzerőt
 * használni) → lifttel elérhető nyugati emelvény (felülről belátni a
 * gyűrűt, jutalom a felderítésért) → a magkamra két ostromgéppel →
 * kijárati zsilip.
 *
 * A geometria C gazdája; az ENCOUNTER-elhelyezés (`things` ellenség-sorai)
 * X-é.
 */

import type { LevelDef } from '../../world/level'
import { box, v } from '../../world/level'

export const LEVEL_3: LevelDef = {
  id: 'l3',
  name: 'Magkamra',
  subtitle: 'A kapu · 7. szint',
  skyColor: 0x140609,
  fogColor: 0x1c0a0e,
  fogDensity: 0.0175,
  ambient: 0.36,

  rooms: [
    {
      id: 1,
      name: 'Ereszkedő',
      // A belépő szándékosan a gyűrű SARKÁBAN van, nem a közepén.
      // Középen a startpozícióból egyenes látóvonal vezetne a magkamrába
      // (belépő-portál → déli folyosó → aréna-portál egy vonalban), és az
      // ostromgépek már a spawn pillanatában lőnék a játékost. Sarokba téve
      // a körfolyosó tömör déli fala takar.
      poly: box(-26, 0, -14, 12),
      floorY: 0,
      ceilY: 5,
      light: 0.55,
      tex: { floor: 'floorTile', ceil: 'ceilingTech', wall: 'techPanel' },
    },

    // --- A körfolyosó négy szakasza -----------------------------------------
    {
      id: 2,
      name: 'Déli körfolyosó',
      poly: box(-20, 12, 20, 20),
      floorY: 0,
      ceilY: 7,
      light: 0.34,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'brick' },
    },
    {
      id: 3,
      name: 'Nyugati körfolyosó',
      poly: box(-20, 20, -12, 44),
      floorY: 0,
      ceilY: 7,
      light: 0.28,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'flesh' },
    },
    {
      id: 4,
      name: 'Keleti körfolyosó',
      poly: box(12, 20, 20, 44),
      floorY: 0,
      ceilY: 7,
      light: 0.32,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'techPanel' },
    },
    {
      id: 5,
      name: 'Északi körfolyosó',
      poly: box(-20, 44, 20, 52),
      floorY: 0,
      ceilY: 7,
      light: 0.34,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'brick' },
    },

    // --- A süllyesztett aréna a gyűrű közepén -------------------------------
    {
      id: 6,
      name: 'Magkamra',
      poly: box(-12, 20, 12, 44),
      floorY: -0.4,
      ceilY: 16,
      light: 0.4,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'flesh' },
    },

    // --- Kulcsterem: zsákutca, ahol a körözés nem működik --------------------
    {
      id: 7,
      name: 'Áldozókamra',
      poly: box(20, 24, 34, 38),
      floorY: 0,
      ceilY: 9,
      light: 0.3,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'flesh' },
    },
    {
      id: 8,
      name: 'Raktárzug',
      poly: box(20, 38, 28, 44),
      floorY: 0,
      ceilY: 6,
      light: 0.24,
      secret: true,
      tex: { floor: 'floorTile', ceil: 'metalRibbed', wall: 'techPanel' },
    },

    // --- Nyugati emelvény lifttel --------------------------------------------
    {
      id: 9,
      name: 'Emelvénylift',
      poly: box(-26, 28, -20, 36),
      floorY: 0,
      ceilY: 10,
      light: 0.3,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'metalPlate' },
      lift: { lowY: 0, highY: 2.4, speed: 1.8, waitTime: 4, startAt: 'low', autoTrigger: true },
    },
    {
      id: 10,
      name: 'Megfigyelő emelvény',
      poly: box(-36, 24, -26, 40),
      floorY: 2.4,
      ceilY: 10,
      light: 0.28,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'metalPlate' },
    },

    // --- Kijárat --------------------------------------------------------------
    {
      id: 11,
      name: 'Kijárati zsilip',
      poly: box(-8, 52, 8, 62),
      floorY: 0,
      ceilY: 6,
      light: 0.6,
      tex: { floor: 'floorTile', ceil: 'lightPanel', wall: 'techPanel' },
    },
  ],

  portals: [
    { id: 1, rooms: [1, 2], p0: v(-20, 12), p1: v(-14, 12) },

    // A gyűrű sarkai — ezek teszik körbejárhatóvá a pályát.
    { id: 2, rooms: [2, 3], p0: v(-20, 20), p1: v(-12, 20) },
    { id: 3, rooms: [2, 4], p0: v(12, 20), p1: v(20, 20) },
    { id: 4, rooms: [5, 3], p0: v(-20, 44), p1: v(-12, 44) },
    { id: 5, rooms: [5, 4], p0: v(12, 44), p1: v(20, 44) },

    // Négy bejárat az arénába — mind a négy irányból támadható és elhagyható.
    { id: 6, rooms: [2, 6], p0: v(-12, 20), p1: v(12, 20) },
    { id: 7, rooms: [5, 6], p0: v(-12, 44), p1: v(12, 44) },
    { id: 8, rooms: [3, 6], p0: v(-12, 20), p1: v(-12, 44) },
    { id: 9, rooms: [4, 6], p0: v(12, 20), p1: v(12, 44) },

    { id: 10, rooms: [4, 7], p0: v(20, 24), p1: v(20, 38) },
    { id: 11, rooms: [4, 8], p0: v(20, 38), p1: v(20, 44) },

    { id: 12, rooms: [3, 9], p0: v(-20, 28), p1: v(-20, 36) },
    // Az emelvény csak a felső liftállásból érhető el.
    { id: 13, rooms: [9, 10], p0: v(-26, 28), p1: v(-26, 36) },

    {
      id: 14,
      rooms: [5, 11],
      p0: v(-8, 52),
      p1: v(8, 52),
      door: { kind: 'door', speed: 2.2, waitTime: 0, key: 'red', tex: 'doorTech' },
    },
  ],

  props: [
    // --- A kapu maga: a magkamra közepén álló, izzó oszlop ------------------
    { id: 1, kind: 'cylinder', pos: v(0, 32), size: v(2.6, 2.6), bottom: -0.4, top: 13, tex: 'techPanel', solid: true },
    { id: 2, kind: 'cylinder', pos: v(0, 32), size: v(3.4, 3.4), bottom: 1.2, top: 2.2, tex: 'hazardStripe', solid: true },
    { id: 3, kind: 'cylinder', pos: v(0, 32), size: v(3.9, 3.9), bottom: 5.5, top: 6.3, tex: 'lightPanel', solid: false, emissive: 1, emissiveColor: 0xff3b1f },
    { id: 4, kind: 'cylinder', pos: v(0, 32), size: v(3.9, 3.9), bottom: 9.5, top: 10.3, tex: 'lightPanel', solid: false, emissive: 1, emissiveColor: 0xff3b1f },

    // --- Az aréna fedezékei. Alacsonyak: takarnak a lövedék elől, de nem
    //     engedik meg, hogy a játékos beássa magát. ---------------------------
    { id: 5, kind: 'box', pos: v(-7, 25), size: v(1.8, 1.1), bottom: -0.4, top: 0.9, tex: 'metalPlate', solid: true },
    { id: 6, kind: 'box', pos: v(7, 25), size: v(1.8, 1.1), bottom: -0.4, top: 0.9, tex: 'metalPlate', solid: true },
    { id: 7, kind: 'box', pos: v(-7, 39), size: v(1.8, 1.1), bottom: -0.4, top: 0.9, tex: 'metalPlate', solid: true },
    { id: 8, kind: 'box', pos: v(7, 39), size: v(1.8, 1.1), bottom: -0.4, top: 0.9, tex: 'metalPlate', solid: true },
    { id: 9, kind: 'cylinder', pos: v(-8, 32), size: v(0.8, 0.8), bottom: -0.4, top: 16, tex: 'flesh', solid: true },
    { id: 10, kind: 'cylinder', pos: v(8, 32), size: v(0.8, 0.8), bottom: -0.4, top: 16, tex: 'flesh', solid: true },
    // További fedezék a két aréna-bejárat vonalában. A záró csoport
    // együttes tűzereje 113 sebzés/mp — ez csak akkor kivédhetetlen, ha
    // nincs mögé bújni. A fedezék nem csökkenti a nehézséget, csak
    // kezelhetővé teszi. (`parbeszed/27-C-brute-dontes.md`)
    { id: 34, kind: 'box', pos: v(0, 23.5), size: v(2.2, 0.7), bottom: -0.4, top: 0.95, tex: 'metalPlate', solid: true },
    { id: 35, kind: 'box', pos: v(0, 40.5), size: v(2.2, 0.7), bottom: -0.4, top: 0.95, tex: 'metalPlate', solid: true },
    { id: 36, kind: 'box', pos: v(-10.5, 28), size: v(0.7, 1.6), bottom: -0.4, top: 0.95, tex: 'metalPlate', solid: true },
    { id: 37, kind: 'box', pos: v(10.5, 36), size: v(0.7, 1.6), bottom: -0.4, top: 0.95, tex: 'metalPlate', solid: true },

    // --- Körfolyosó: szabályos oszlopsor, ami futás közben megtöri a
    //     látóvonalat — ettől lesz a körözés valódi taktika, nem futópálya. --
    { id: 11, kind: 'cylinder', pos: v(-16, 25), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 12, kind: 'cylinder', pos: v(-16, 33), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 13, kind: 'cylinder', pos: v(-16, 41), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 14, kind: 'cylinder', pos: v(16, 25), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 15, kind: 'cylinder', pos: v(16, 33), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 16, kind: 'cylinder', pos: v(16, 41), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 17, kind: 'cylinder', pos: v(-15, 16), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 18, kind: 'cylinder', pos: v(15, 16), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 19, kind: 'cylinder', pos: v(-15, 48), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },
    { id: 20, kind: 'cylinder', pos: v(15, 48), size: v(0.7, 0.7), bottom: 0, top: 7, tex: 'metalRibbed', solid: true },

    // --- Áldozókamra: oltár, amin a kulcs áll ------------------------------
    { id: 21, kind: 'box', pos: v(30, 31), size: v(1.6, 1.6), bottom: 0, top: 0.4, tex: 'flesh', solid: true },
    { id: 22, kind: 'box', pos: v(30, 31), size: v(1.1, 1.1), bottom: 0.4, top: 0.8, tex: 'hazardStripe', solid: true },
    { id: 23, kind: 'cylinder', pos: v(23, 27), size: v(0.6, 0.6), bottom: 0, top: 9, tex: 'flesh', solid: true },
    { id: 24, kind: 'cylinder', pos: v(23, 35), size: v(0.6, 0.6), bottom: 0, top: 9, tex: 'flesh', solid: true },

    // --- Emelvény korlátja --------------------------------------------------
    { id: 25, kind: 'box', pos: v(-26.15, 32), size: v(0.15, 5.6), bottom: 2.4, top: 3.4, tex: 'metalRibbed', solid: true },

    // --- Világítás-testek ---------------------------------------------------
    { id: 26, kind: 'box', pos: v(-19.7, 32), size: v(0.15, 3), bottom: 4.4, top: 5.4, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 27, kind: 'box', pos: v(19.7, 32), size: v(0.15, 3), bottom: 4.4, top: 5.4, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 28, kind: 'box', pos: v(-17, 12.3), size: v(2.4, 0.15), bottom: 4.4, top: 5.4, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 29, kind: 'box', pos: v(0, 51.7), size: v(3, 0.15), bottom: 4.4, top: 5.4, tex: 'lightPanel', solid: false, emissive: 1 },

    // --- Kezdő és záró jelzések --------------------------------------------
    { id: 30, kind: 'box', pos: v(-17, 11.4), size: v(2.4, 0.3), bottom: 0, top: 0.35, tex: 'hazardStripe', solid: false },
    { id: 31, kind: 'box', pos: v(0, 61.6), size: v(2.6, 0.3), bottom: 0, top: 3, tex: 'hazardStripe', solid: false },
    { id: 32, kind: 'cylinder', pos: v(-24, 3), size: v(0.6, 0.6), bottom: 0, top: 2.4, tex: 'metalRibbed', solid: true },
    { id: 33, kind: 'cylinder', pos: v(-16, 3), size: v(0.6, 0.6), bottom: 0, top: 2.4, tex: 'metalRibbed', solid: true },
  ],

  lights: [
    { pos: v(-20, 3), y: 4.2, color: 0xffd98a, intensity: 11, distance: 15, style: 'steady' },
    { pos: v(-19, 10), y: 4.4, color: 0x64c4cf, intensity: 7, distance: 11, style: 'flicker' },

    // A kapu vörös lüktetése uralja az arénát — ez a pálya vizuális horgonya.
    { pos: v(0, 32), y: 6, color: 0xff3b1f, intensity: 18, distance: 30, style: 'pulse' },
    { pos: v(0, 32), y: 10.5, color: 0xff3b1f, intensity: 12, distance: 24, style: 'pulse' },
    { pos: v(-8, 24), y: 5, color: 0xe4b02c, intensity: 9, distance: 16, style: 'steady' },
    { pos: v(8, 40), y: 5, color: 0xe4b02c, intensity: 9, distance: 16, style: 'steady' },

    // Körfolyosó: ritkás, hideg fény — a gyűrű szándékosan sötétebb, mint
    // a közép, hogy a játékos ösztönösen a fény felé tartson.
    { pos: v(-16, 16), y: 5, color: 0xffd98a, intensity: 7, distance: 13, style: 'flicker' },
    { pos: v(16, 16), y: 5, color: 0x64c4cf, intensity: 7, distance: 13, style: 'steady' },
    { pos: v(-16, 32), y: 5, color: 0xa53347, intensity: 7, distance: 14, style: 'pulse' },
    { pos: v(16, 32), y: 5, color: 0x64c4cf, intensity: 8, distance: 14, style: 'steady' },
    { pos: v(-16, 48), y: 5, color: 0xffd98a, intensity: 7, distance: 13, style: 'flicker' },
    { pos: v(16, 48), y: 5, color: 0xffd98a, intensity: 7, distance: 13, style: 'steady' },

    { pos: v(28, 31), y: 6, color: 0xa53347, intensity: 10, distance: 18, style: 'pulse' },
    { pos: v(24, 41), y: 4.5, color: 0x8affff, intensity: 6, distance: 10, style: 'pulse' },
    { pos: v(-31, 32), y: 7, color: 0x64c4cf, intensity: 9, distance: 16, style: 'steady' },
    { pos: v(-23, 32), y: 6, color: 0x64c4cf, intensity: 7, distance: 11, style: 'flicker' },
    { pos: v(0, 57), y: 5, color: 0xffffff, intensity: 14, distance: 16, style: 'steady' },
  ],

  things: [
    { kind: 'playerStart', type: 'player', pos: v(-20, 4), yaw: 0 },

    // === ENCOUNTEREK — gazda: X ==========================================
    // GDD 4. pont, 3. pálya: 39 ellenfél
    // (10 grunt, 10 imp, 9 hound, 7 sentinel, 3 brute).
    // C a térnek megfelelő kiinduló elrendezést adta; a hangolás X-é.

    // Bemelegítés a déli folyosón: 3 imp + 2 grunt.
    { kind: 'enemy', type: 'imp', pos: v(-13, 16), yaw: 1.6, group: 'a' },
    { kind: 'enemy', type: 'imp', pos: v(13, 16), yaw: -1.6, group: 'a' },
    { kind: 'enemy', type: 'imp', pos: v(0, 18), yaw: Math.PI, group: 'a' },
    { kind: 'enemy', type: 'grunt', pos: v(-18, 17), yaw: 1.6, group: 'a' },
    { kind: 'enemy', type: 'grunt', pos: v(18, 17), yaw: -1.6, group: 'a' },

    // Körfolyosó: 3 hound + 2 sentinel — a hound hajt, a sentinel bünteti,
    // ha megállsz. Ez tanítja meg a körözést.
    { kind: 'enemy', type: 'hound', pos: v(-16, 28), yaw: Math.PI, group: 'b' },
    { kind: 'enemy', type: 'hound', pos: v(-16, 37), yaw: Math.PI, group: 'b' },
    { kind: 'enemy', type: 'hound', pos: v(16, 36), yaw: 0, group: 'b' },
    { kind: 'enemy', type: 'sentinel', pos: v(-15, 46), yaw: Math.PI, group: 'b' },
    { kind: 'enemy', type: 'sentinel', pos: v(15, 46), yaw: Math.PI, group: 'b' },

    // Áldozókamra: az első ostromgép, zsákutcában.
    { kind: 'enemy', type: 'brute', pos: v(30, 34), yaw: -1.6, group: 'c' },
    { kind: 'enemy', type: 'imp', pos: v(24.5, 26), yaw: -1.6, group: 'c' },
    { kind: 'enemy', type: 'imp', pos: v(24.5, 36), yaw: -1.6, group: 'c' },
    { kind: 'enemy', type: 'imp', pos: v(32, 26), yaw: -1.6, group: 'c' },
    { kind: 'enemy', type: 'imp', pos: v(32.5, 32), yaw: -1.6, group: 'c' },
    { kind: 'enemy', type: 'grunt', pos: v(32, 36), yaw: -1.6, group: 'c' },
    { kind: 'enemy', type: 'grunt', pos: v(26, 30), yaw: -1.6, group: 'c' },
    { kind: 'enemy', type: 'grunt', pos: v(26, 33), yaw: -1.6, group: 'c' },

    // Emelvényharc: 3 sentinel magasból, a nyugati emelvényen.
    { kind: 'enemy', type: 'sentinel', pos: v(-31, 28), yaw: 0, group: 'd' },
    { kind: 'enemy', type: 'sentinel', pos: v(-31, 36), yaw: 0, group: 'd' },
    { kind: 'enemy', type: 'sentinel', pos: v(-34, 32), yaw: 0, group: 'd' },

    // Végső magkamra: 2 ostromgép + kísérők. A teljes eszköztárat kéri.
    { kind: 'enemy', type: 'brute', pos: v(-7, 30), yaw: 0, group: 'e' },
    { kind: 'enemy', type: 'brute', pos: v(7, 34), yaw: Math.PI, group: 'e' },
    { kind: 'enemy', type: 'hound', pos: v(-9, 22), yaw: 0, group: 'e' },
    { kind: 'enemy', type: 'hound', pos: v(9, 22), yaw: 0, group: 'e' },
    { kind: 'enemy', type: 'hound', pos: v(-9, 42), yaw: Math.PI, group: 'e' },
    { kind: 'enemy', type: 'imp', pos: v(-10, 36), yaw: 0, group: 'e' },
    { kind: 'enemy', type: 'imp', pos: v(10, 28), yaw: Math.PI, group: 'e' },
    { kind: 'enemy', type: 'imp', pos: v(0, 42), yaw: Math.PI, group: 'e' },

    // Kijárati őrség az északi folyosón.
    { kind: 'enemy', type: 'grunt', pos: v(-8, 48), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'grunt', pos: v(-3, 48), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'grunt', pos: v(3, 48), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'grunt', pos: v(8, 48), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'grunt', pos: v(0, 50.5), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'hound', pos: v(-12, 48), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'hound', pos: v(12, 48), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'hound', pos: v(-18, 48), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'sentinel', pos: v(-8, 50.5), yaw: Math.PI, group: 'f' },
    { kind: 'enemy', type: 'sentinel', pos: v(8, 50.5), yaw: Math.PI, group: 'f' },

    // === LŐSZER ÉS ÉLET (GDD 3. pont, 3. pálya) ==========================
    { kind: 'pickup', type: 'bullets', pos: v(-23, 6) },
    { kind: 'pickup', type: 'shells', pos: v(-17, 6) },
    { kind: 'pickup', type: 'medkitSmall', pos: v(-20, 8) },

    { kind: 'pickup', type: 'bullets', pos: v(-18, 14) },
    { kind: 'pickup', type: 'cells', pos: v(-18, 24) },
    { kind: 'pickup', type: 'bullets', pos: v(18, 42) },

    // A magkamra sarkaiban a nehéz lőszer — érte be kell menni az arénába.
    { kind: 'pickup', type: 'rockets', pos: v(10, 42) },
    { kind: 'pickup', type: 'medkitLarge', pos: v(0, 26) },
    { kind: 'pickup', type: 'medkitSmall', pos: v(0, 20) },

    // Az áldozókamra tartja a vörös kulcsot.
    { kind: 'pickup', type: 'keyRed', pos: v(30, 31) },
    { kind: 'pickup', type: 'shells', pos: v(33, 26) },

    // Titkos raktárzug: a plazmavető.
    { kind: 'pickup', type: 'weapon:plasma', pos: v(24, 41) },
    { kind: 'pickup', type: 'cells', pos: v(-28, 36) },

    // Az emelvény: nagy medkit + páncél a felderítésért.
    { kind: 'pickup', type: 'medkitLarge', pos: v(-31, 32) },
    { kind: 'pickup', type: 'armor', pos: v(-34, 27) },
    { kind: 'pickup', type: 'rockets', pos: v(-34, 37) },

    // Kijárat előtti utolsó utánpótlás.
    { kind: 'pickup', type: 'bullets', pos: v(-4, 56) },
    { kind: 'pickup', type: 'bullets', pos: v(4, 55) },

    { kind: 'exit', type: 'exit', pos: v(0, 60) },
  ],
}
